import { API } from "./api";
import { ProjectCreatePayload } from "../features/projects/domain/payload";
import { fetchWithAuth } from "./http";
import { ProjectsResponse } from "../features/common/domain/types";
import {
  ProjectDetailApiResponse,
  ProjectPatchApiResponse,
  ProjectDetail,
  flattenProjectDetail,
  flattenProjectPatch,
} from "../features/projects/domain/types";
import type { AirshedFeatureCollection, IntensityTileResponse } from "../features/model/domain/types";

export const getModelRunsByCustomer = (customerId: string, token: string) =>
  fetchWithAuth<any[]>(`/model-runs/by-customer/${customerId}`, token);

export async function createProject(payload: ProjectCreatePayload, token: string) {
  return fetchWithAuth<{ project_id: string }>(`/projects`, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function queueRun(projectId: string, token: string, analysisYear?: number) {
  const body: Record<string, any> = {};
  // Default to last full calendar year for existing projects
  if (analysisYear) {
    body.analysis_year = analysisYear;
  } else {
    body.analysis_year = new Date().getFullYear() - 1;
  }
  return fetchWithAuth<{ run_id: string }>(`/projects/${projectId}/queue`, token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function getLatestRun(projectId: string, token: string, scenarioId?: string | null) {
  const params = scenarioId ? `?scenario_id=${scenarioId}` : "";
  const r = await fetch(`${API}/projects/${projectId}/runs/latest${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (r.status === 401) {
    // Delegate to the shared expired-session handler
    const { handleExpiredSession } = await import("./http");
    handleExpiredSession();
    return null;
  }
  if (!r.ok) return null;
  return r.json();
}

export async function getAll(token: string) {
  return fetchWithAuth<any>(`/projects`, token);
}

export const getByCustomer = (customerId: string, token: string) =>
  fetchWithAuth<ProjectsResponse>(`/admin/projects/by-customer/${customerId}`, token);

/**
 * Get project by ID with flattened config.
 * Transforms nested API response to flat structure for components.
 */
export const getById = async (projectId: string, token: string): Promise<ProjectDetail> => {
  const response = await fetchWithAuth<ProjectDetailApiResponse>(`/projects/${projectId}`, token);
  return flattenProjectDetail(response);
};

/**
 * Update project with flattened response.
 * Transforms nested API response to flat structure for components.
 */
export const updateProject = async (projectId: string, data: Partial<ProjectDetail>, token: string) => {
  const response = await fetchWithAuth<ProjectPatchApiResponse>(`/projects/${projectId}`, token, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  return flattenProjectPatch(response);
};

export const getRun = (runId: string, token: string) =>
  fetchWithAuth<any>(`/runs/${runId}`, token);

export const deleteProject = (projectId: string, projectName: string, token: string) =>
  fetchWithAuth<{ project_id: string; message: string }>(`/admin/projects/${projectId}`, token, {
    method: 'DELETE',
    body: JSON.stringify({ project_name_confirmation: projectName }),
  });

/**
 * Fetch per-plant airshed polygons (InMAP-derived) for a project.
 * Empty FeatureCollection when the polygon cache isn't warm yet — callers
 * should fall back to circular buffers for plants without a polygon.
 */
export async function fetchProjectAirsheds(
  projectId: string,
  token: string,
): Promise<AirshedFeatureCollection> {
  return fetchWithAuth<AirshedFeatureCollection>(
    `/projects/${projectId}/airsheds`,
    token,
  );
}

/**
 * Phase 4b: fetch the receptor-sampled PM2.5 intake tile manifest for a
 * project. Returns null when the backend responds 404 (tiles haven't
 * been built yet) so the caller can fall back to the polygon layer.
 */
export async function fetchProjectIntensityTiles(
  projectId: string,
  token: string,
): Promise<IntensityTileResponse | null> {
  const r = await fetch(`${API}/projects/${projectId}/airshed-intensity-tiles`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (r.status === 401) {
    const { handleExpiredSession } = await import("./http");
    handleExpiredSession();
    return null;
  }
  if (r.status === 404) return null;
  if (!r.ok) {
    const text = await r.text();
    throw new Error(text || `Intensity tiles: ${r.status}`);
  }
  return r.json();
}

export const renameProject = (projectId: string, newName: string, token: string) =>
  fetchWithAuth<{ project_id: string; old_name: string; new_name: string; message: string }>(
    `/admin/projects/${projectId}/rename`,
    token,
    {
      method: 'PATCH',
      body: JSON.stringify({ new_name: newName }),
    },
  );
